import React from 'react';
import { cn } from '../commons/utils';
import { THEME_V2_CLASS } from '@v2/constants';
import { textVariants } from './text-variants';
import type { CommonProps } from '../types';

type TextVariant =
  | 'subtitle_l'
  | 'subtitle'
  | 'subtitle_s'
  | 'body_xl'
  | 'body_l'
  | 'body'
  | 'body_s'
  | 'label'
  | 'label_md'
  | 'label_s'
  | 'label_xs'
  | 'label_l'
  | 'code';
type TextWeight = 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
type FontFamily = 'Roboto' | 'Poppins' | 'Fira';
type TextTransform = 'uppercase' | 'lowercase' | 'capitalize';
type TextSkin =
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'light'
  | 'brand'
  | 'destructive'
  | 'warning'
  | 'success'
  | 'tertiary'
  | 'white';
type TextAlign = 'left' | 'center' | 'right' | 'justify' | 'start' | 'end';
type TextOwnProps = {
  /** Content of the text */
  children?: React.ReactNode;
  /** Font weight */
  weight?: TextWeight;
  /** Font family */
  fontFamily?: FontFamily;
  /** Color skin — semantic token name
   * @default 'primary'
   */
  skin?: TextSkin;
  /** Typography variant
   * @default 'body'
   */
  variant?: TextVariant;
  /** Override font size */
  fontSize?: React.CSSProperties['fontSize'];
  /** Text alignment
   * @default 'left'
   */
  textAlign?: TextAlign;
  /** Line height */
  lineHeight?: React.CSSProperties['lineHeight'];
  /** Letter spacing */
  letterSpacing?: React.CSSProperties['letterSpacing'];
  /** Text transform */
  transform?: TextTransform;
  /** Whether to underline the text */
  underline?: boolean;
} & CommonProps;

export type TextProps<T extends React.ElementType = 'p'> = TextOwnProps & {
  /** HTML element to render
   * @default 'p'
   */
  as?: T;
  /** Ref forwarded to the underlying HTML element */
  ref?: React.Ref<HTMLElement>;
} & Omit<React.ComponentPropsWithoutRef<T>, keyof TextOwnProps | 'as' | 'ref'>;

const fontFamilyMap: Record<TextVariant, FontFamily> = {
  subtitle_l: 'Roboto',
  subtitle: 'Roboto',
  subtitle_s: 'Roboto',
  body_xl: 'Roboto',
  body_l: 'Roboto',
  body: 'Roboto',
  body_s: 'Roboto',
  label: 'Poppins',
  label_md: 'Poppins',
  label_s: 'Poppins',
  label_xs: 'Poppins',
  label_l: 'Poppins',
  code: 'Fira',
};

const Text = <T extends React.ElementType = 'p'>({
  ref,
  children,
  weight,
  fontFamily,
  skin = 'primary',
  variant = 'body',
  fontSize,
  textAlign,
  lineHeight,
  letterSpacing,
  transform,
  underline,
  as,
  theme,
  testId,
  className,
  ...nativeProps
}: TextProps<T>) => {
  const Component: React.ElementType = as || 'p';
  return (
    <Component
      data-testid={testId}
      ref={ref as React.Ref<never>}
      {...nativeProps}
      className={cn(
        THEME_V2_CLASS,
        theme,
        textVariants({
          variant,
          skin,
          weight,
          fontFamily: fontFamily ?? fontFamilyMap[variant],
          textAlign,
          transform,
          underline,
        }),
        className
      )}
      style={{
        ...(fontSize && { fontSize }),
        ...(lineHeight && { lineHeight }),
        ...(letterSpacing && { letterSpacing }),
      }}
    >
      {children}
    </Component>
  );
};

Text.displayName = 'Text';

export { Text };
